import React, { useCallback, useEffect, useRef } from 'react';

import { useNavigation } from '@react-navigation/native';

import { PolymarketConfirmationDisclaimer } from '@/components/PolymarketDisclaimer';
import { EntryConfirmation } from '@/components/checkout/EntryConfirmation';
import { EntryConfirmationBackground } from '@/components/checkout/EntryConfirmationBackground';
import { Box } from '@/components/lib/components';
import { sbkBetslipPromoKeys } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-promos';
import { useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { BetSuccessButtons } from '@/feature/betslip-sbk/screens/components/BetSuccessButtons';
import { usePushPrimerEligibility } from '@/feature/push-primer/hooks/use-push-primer-eligibility';
import { useIsSweepsProduct } from '@/hooks/use-is-sweeps-product';
import { Product } from '@/hooks/use-jurisdiction';
import { usePreventAndroidBackNavigation } from '@/hooks/use-prevent-android-back-navigation';
import { runStoreUpdate } from '@/utils/zustand';
import { useQueryClient } from '@tanstack/react-query';

import { BetSuccessNavigationProp } from '../types';
import { useBetSuccessData } from './hooks/use-bet-success-data';
import { useBetSuccessInfo } from './hooks/use-bet-success-info';

export const BetSuccessScreen = () => {
    const navigation = useNavigation<BetSuccessNavigationProp>();
    const queryClient = useQueryClient();
    const isSweeps = useIsSweepsProduct();

    const betSuccessData = useBetSuccessData();
    const { hasCombo, betIds } = betSuccessData;
    const { title, rows } = useBetSuccessInfo(betSuccessData);
    const isMultipleEntryType = betSuccessData.entryType === 'multiple';

    const pushPrimerEligibility = usePushPrimerEligibility();
    const clearBetSlip = useSbkBetSlipStore(state => state.actions.clearBetSlip);
    const keepUnstakedSelections = useSbkBetSlipStore(state => state.actions.keepUnstakedSelections);

    const keepBetDataWhenScreenRemoved = useRef(false);

    const cleanupBetSlip = useCallback(() => {
        if (hasCombo) {
            runStoreUpdate(clearBetSlip);
        } else {
            runStoreUpdate(keepUnstakedSelections);
        }
    }, [hasCombo, clearBetSlip, keepUnstakedSelections]);

    // Clean up the betslip on any navigation away from this screen, including dismiss from top.
    // Buttons that handle cleanup explicitly set keepBetDataWhenScreenRemoved to prevent double-cleanup.
    useEffect(() => {
        return navigation.addListener('beforeRemove', () => {
            queryClient.invalidateQueries({ queryKey: sbkBetslipPromoKeys.all });
            if (keepBetDataWhenScreenRemoved.current) return;
            cleanupBetSlip();
        });
    }, [navigation, cleanupBetSlip, queryClient]);

    const handleDone = useCallback(() => {
        keepBetDataWhenScreenRemoved.current = true;
        cleanupBetSlip();
        navigation.getParent()?.goBack();

        if (pushPrimerEligibility) {
            setTimeout(() => {
                navigation.navigate('PushPrimer');
            }, 500);
        }
    }, [cleanupBetSlip, navigation, pushPrimerEligibility]);

    usePreventAndroidBackNavigation();

    return (
        <EntryConfirmationBackground>
            <EntryConfirmation
                onClose={handleDone}
                title={title}
                product={isSweeps ? Product.Sweeps : Product.Prediction}
                rows={rows}
                banner={
                    isSweeps ? undefined : (
                        <Box opacity={0.4}>
                            <PolymarketConfirmationDisclaimer />
                        </Box>
                    )
                }
                buttons={
                    <BetSuccessButtons
                        hasCombo={hasCombo}
                        betIds={betIds}
                        isMultipleEntryType={isMultipleEntryType}
                        pushPrimerEligibility={pushPrimerEligibility}
                        keepBetDataWhenScreenRemoved={keepBetDataWhenScreenRemoved}
                        onDone={handleDone}
                    />
                }
            />
        </EntryConfirmationBackground>
    );
};
